/* global SENTRY_DSN */
import React from 'react';
import ReactDOM from 'react-dom';
import { OidcProvider } from '@axa-fr/react-oidc';
import { ConfigCatProvider, PollingMode } from 'configcat-react';
import { ErrorBoundary } from '@sentry/react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import {
  // API,
  // Faro,
  getWebInstrumentations,
  initializeFaro,
  ReactIntegration,
  withFaroProfiler,
} from '@grafana/faro-react';
import { set } from 'lodash';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IntercomProvider } from 'react-use-intercom';

import './index.scss';
import { authConfig, IntercomAppId } from './configuration';

import App from './component/App';
import { SessionLost } from './component/Authentication';
import { AppShellErrorPage as ErrorPage } from './component/ErrorPage';
import NavLoading from './component/NavLoading';
import { initSentry } from './initSentry';
import { EmitterProvider } from './hooks/useEmitter';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const { version } = require('../package.json');

if (SENTRY_DSN !== '') {
  initSentry();
}

const ENVIRONMENTS = {
  local: 'dev',
  beta: 'qa',
  live: 'prod',
  test: 'test',
};

const FARO_COLLECTOR_URL = LOCAL_MONITORING
  ? 'http://localhost:1234/collect'
  : 'https://eks-prod-appshell.otlp-frontend.mintel.cloud/collect';

/**
 * URLs we should propagate traces to (include traceparent headers in request)
 * **WARNING:**
 *    Ensure these services allow the traceparent header in Access-Control-Allow-Headers field
 *    These requests will be blocked otherwise
 */
const PROPAGATE_URLS: RegExp[] = [
  // /https:\/\/.+\.mintel\.com.+/
  /https:\/\/(qa-)?portal\.mintel\.com.+/,
];

if (BUILD_TARGET !== 'test') {
  initializeFaro({
    url: FARO_COLLECTOR_URL,
    apiKey: 'd9c8b2ac-a604-11ed-973d-e365fd5c9afe',
    app: {
      name: 'frontend-app-shell',
      version,
      environment: ENVIRONMENTS[BUILD_TARGET],
    },
    instrumentations: [
      ...getWebInstrumentations({
        captureConsole: true,
      }),
      new TracingInstrumentation({
        instrumentationOptions: {
          propagateTraceHeaderCorsUrls: PROPAGATE_URLS,
        },
      }),
      new ReactIntegration(),
    ],
  });
}

if (window?.faro?.api) {
  window.faro.api.withFaroProfiler = withFaroProfiler;
} else {
  set(
    window,
    'faro.api.withFaroProfiler',
    (component: React.ComponentType): React.FC => component as React.FC,
  );
}

if (BUILD_TARGET === 'test') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
  const { worker } = require('../mocks/browser');
  worker.start();
}

const queryClient = new QueryClient();

ReactDOM.render(
  <React.StrictMode>
    <ErrorBoundary
      fallback={({ error }) => (
        <NavLoading>
          <ErrorPage errorCode={(error as CustomHttpError).statusCode} />
        </NavLoading>
      )}
    >
      <EmitterProvider>
        <QueryClientProvider client={queryClient}>
          <OidcProvider
            authenticatingComponent={() => <NavLoading />}
            callbackSuccessComponent={() => <NavLoading />}
            loadingComponent={() => <NavLoading />}
            sessionLostComponent={SessionLost}
            configuration={authConfig}
          >
            <ConfigCatProvider
              sdkKey={CONFIGCAT_PROJECT_KEY}
              pollingMode={PollingMode.LazyLoad}
              options={{ cacheTimeToLiveSeconds: 60 * 15 }}
            >
              <IntercomProvider appId={IntercomAppId} autoBoot>
                <App />
              </IntercomProvider>
            </ConfigCatProvider>
          </OidcProvider>
        </QueryClientProvider>
      </EmitterProvider>
    </ErrorBoundary>
  </React.StrictMode>,
  document.getElementById('root'),
);
